/** 濁点・半濁点などの結合文字を合成形（NFC）に統一する。
 *  Excel コピペ（NFD）と IME 手入力（NFC）の表記ゆれを吸収し、
 *  ショット名・タスク名の文字列照合が見た目どおり一致するようにする。 */
export const normalizeNfc = (value: string): string =>
  (value ?? "").normalize("NFC");

/** メンションの表示名として許可する文字の並び（空白・区切り記号・括弧類で終端） */
const MENTION_NAME_SRC = "[^\\s@#、。,.!?！？(（)）[\\]［］「」【】]+";

/**
 * メールアドレス・URL を構成しうる文字。`@` の直前がこれらのときは
 * 誤検出防止の追加条件（表示名が非 ASCII で始まる / 直後に数量が続く）を課す。
 */
const MENTION_EMAILISH_CHARS = "A-Za-z0-9._%+\\-/:";

/** 数量括弧の始まり（開き括弧 + 数字）。先読みでの数量有無の判定に使う */
const MENTION_AMOUNT_HEAD_SRC = "[[［「【]\\s*[\\d０-９]";

/** 数量部分（`[3h]` 等、省略可）。`m[3]` = 数量、`m[4]` = 単位 */
const MENTION_AMOUNT_SRC =
  "(?:[[［「【]\\s*([\\d０-９]+(?:[.．][\\d０-９]+)?)\\s*(h|H|ｈ|Ｈ|時間|m|M|ｍ|Ｍ|分)?\\s*[\\]］」】])?";

/**
 * 直前の 1 文字（グループ 1）。次のいずれかでメンションとみなす:
 * - 行頭
 * - メール・URL に使われない文字（空白・括弧のほか、日本語文字なども含む。
 *   `立ち会い@善里` のように空白を挟まない書き方を認識するため）
 * - メール・URL に使われうる文字でも、`@` の直後が非 ASCII（日本語名）か、
 *   表示名直後に `[数量` が続く場合（`9/18@善里[1h]` / `M01@DAI[2h]`）。
 *   これにより `foo@example.com` や URL 中の `@user` は拾わない
 */
const MENTION_PREFIX_SRC =
  `(^|[^${MENTION_EMAILISH_CHARS}]|[${MENTION_EMAILISH_CHARS}]` +
  `(?=@(?:[^\\x20-\\x7E]|${MENTION_NAME_SRC}${MENTION_AMOUNT_HEAD_SRC})))`;

const MENTION_REGEX_SRC = `${MENTION_PREFIX_SRC}@(${MENTION_NAME_SRC})${MENTION_AMOUNT_SRC}`;

/**
 * `@表示名` メンションの正規表現を生成する。表示名の直後に括弧で数量を続けると
 * 作業時間（アクティビティ）として解釈する: `@山田[3h]`。
 *
 * - 直前が行頭、または英数字・メール/URL 記号（`A-Za-z0-9._%+-/:`）以外の文字なら
 *   メンションとみなす（`当日立ち会い@善里[14h]` のような空白なしの記入も認識する）
 * - 直前が英数字等でも、表示名が非 ASCII（日本語名）で始まるか、表示名直後に
 *   `[数量]` が続く場合は認識する（`9/18@善里[1h]` / `M01@DAI[2h]`）。
 *   メールアドレス（`foo@example.com`）や URL 中の `@` は引き続き拾わない
 * - `m[1]` = 直前の区切り文字（行頭マッチ時は空文字）、`m[2]` = 表示名、
 *   `m[3]` = 数量（省略可）、`m[4]` = 単位（省略可）
 * - 数量の括弧は表示名に**直付け**のときだけ拾う（`@山田 [3]` のような脚注表記を
 *   誤って集計しないため）
 * - 日本語 IME では `[` が `「` や全角 `［` になるため、開き括弧・閉じ括弧・数字は
 *   全角も許容する。単位も `h` / `時間` / `m` / `分` の全角半角を受ける
 * - Safari 対応のため後読み（lookbehind）は使わず、直前 1 文字を `m[1]` として
 *   消費する。そのため `@善里[1h]@稲元[1h]` のように区切りなく連続する 2 つ目は
 *   拾えない（既知の制約）
 * - グローバルフラグ付きで `lastIndex` の状態を持つため、共有インスタンスではなく
 *   **使用の都度この関数で生成する**こと
 *
 * タスクボードの担当者抽出（taskBoardUtils）と Markdown 本文のメンション装飾
 * （MarkdownContent）で同じ規則を共有する。
 */
export const createMentionRegex = (): RegExp => new RegExp(MENTION_REGEX_SRC, "g");

/** 全角英数字・全角小数点を半角に落とす（メンション数量のパース用） */
const toHalfWidth = (value: string): string =>
  value
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/．/g, ".");

/** 分を表す単位（これ以外は時間として扱う） */
const MINUTE_UNITS = new Set(["m", "M", "ｍ", "Ｍ", "分"]);

/**
 * メンション数量（`createMentionRegex` の `m[3]` / `m[4]`）を時間に正規化する。
 * 数量が無い・0 以下・解釈できない場合は null（= 集計対象外）。
 *
 * 単位は省略可能で、省略時は時間として扱う（入力を軽くするため）。ただし UI・
 * ヘルプでは常に `[3h]` の形で案内する。
 */
export const parseMentionAmountHours = (
  rawAmount: string | undefined,
  rawUnit: string | undefined,
): number | null => {
  if (!rawAmount) return null;
  const value = Number(toHalfWidth(rawAmount));
  if (!Number.isFinite(value) || value <= 0) return null;
  return rawUnit && MINUTE_UNITS.has(rawUnit) ? value / 60 : value;
};
